import { Breadcrumb, DateField, EditButton, ReferenceField, Show, TextField } from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, FileText, Mail, PackageCheck } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CrmDataProvider } from '../../providers/types';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import { DeliveryChallan } from '../../types';
import { AddressBlock } from '../shared/AddressBlock';
import { WatermarkOverlay } from '../shared/WatermarkOverlay';
import { DCEmailModal } from './DCEmailModal';
import { DCPDFButton } from './DCPDFButton';

// X-3: render the linked contact's phone (phone_jsonb) when no manual override.
// Rendered inside <ReferenceField reference="contacts">, so the contact is in context.
const DCContactPhone = () => {
    const contact = useRecordContext() as any;
    const phones = Array.isArray(contact?.phone_jsonb) ? contact.phone_jsonb : [];
    const numbers = phones.map((p: any) => p?.number).filter(Boolean).join(', ');
    return <>{numbers || '-'}</>;
};

// ─── CSS (mirrors PIShow.tsx / QuotationShow.tsx po-sheet style) ──────────────
const css = `
  .dc-wrap {
    font-family: Arial, sans-serif;
    font-size: 12px;
    color: #000;
    background: #e8eaf0;
    min-height: 100vh;
    padding: 28px 20px 48px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Backdrop follows the app theme; the sheet itself stays paper-white. */
  .dark .dc-wrap {
    background: var(--muted);
  }

  .dc-sheet {
    background: #fff;
    max-width: 960px;
    margin: 0 auto;
    padding: 20px 24px 24px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.12);
    border: 1px solid #ccc;
    position: relative;
  }

  /* ── HEADER BAR: 2-col (logo+company | title+meta) ── */
  .dc-header-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border: 1.5px solid #000;
    align-items: stretch;
  }
  .dc-header-logo {
    padding: 12px 16px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .dc-header-logo img {
    height: 44px;
    width: auto;
    align-self: flex-start;
    display: block;
    margin-bottom: 6px;
  }
  .dc-company-name {
    font-size: 14px;
    font-weight: 800;
    color: #1a3a6e;
    letter-spacing: 0.02em;
    margin-bottom: 2px;
  }
  .dc-company-addr {
    font-size: 9px;
    color: #555;
    line-height: 1.5;
  }
  .dc-header-right {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 16px;
  }
  .dc-header-title {
    font-size: 24px;
    font-weight: 800;
    color: #1a3a6e;
    letter-spacing: 0.06em;
    margin-bottom: 6px;
  }
  .dc-challan-no {
    font-size: 13px;
    font-weight: 700;
    color: #333;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
  }
  .dc-meta-grid {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 3px 0;
    font-size: 11px;
  }
  .dc-meta-lbl { font-weight: 700; color: #555; text-transform: uppercase; }
  .dc-meta-val { font-weight: 700; color: #000; }

  /* ── BILL TO / CONTACT ── */
  .dc-bill-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border: 1.5px solid #000;
    border-top: none;
    border-bottom: 1.5px solid #000;
    position: relative;
    z-index: 10;
  }
  .dc-bill-left {
    padding: 10px 14px;
    border-right: 1.5px solid #000;
  }
  .dc-bill-right {
    padding: 10px 14px;
  }
  .dc-section-lbl {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #555;
    background: #f0f0f0;
    padding: 3px 8px;
    margin-bottom: 6px;
    margin-left: -14px;
    margin-right: -14px;
    margin-top: -10px;
    border-bottom: 1px solid #ddd;
  }
  .dc-bill-company {
    font-size: 13px;
    font-weight: 800;
    color: #1a3a6e;
    margin-bottom: 4px;
  }
  .dc-bill-addr {
    font-size: 11px;
    color: #333;
    line-height: 1.6;
  }
  .dc-contact-row {
    display: flex;
    gap: 4px;
    font-size: 11px;
    margin-top: 4px;
  }
  .dc-contact-lbl { font-weight: 700; min-width: 110px; color: #555; }
  .dc-contact-val { color: #000; }

  /* ── PLACE OF SUPPLY ── */
  .dc-supply-bar {
    border: 1.5px solid #000;
    border-top: none;
    border-bottom: none;
    display: grid;
    grid-template-columns: 140px 1fr;
    font-size: 11px;
  }
  .dc-supply-lbl {
    font-weight: 700;
    padding: 6px 12px;
    border-right: 1.5px solid #000;
    background: #f0f0f0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 10px;
    display: flex;
    align-items: center;
  }
  .dc-supply-val { padding: 6px 12px; line-height: 1.5; }

  /* ── LOGISTICS BAR ── */
  .dc-logistics-bar { border: 1.5px solid #000; border-top: 1.5px solid #000; display: flex; position: relative; z-index: 9; }
  .dc-bill-bar + .dc-logistics-bar { border-top: none; }
  .dc-logistics-col { flex: 1; border-right: 1.5px solid #000; display: flex; flex-direction: column; }
  .dc-logistics-col:last-child { border-right: none; }
  .dc-logistics-lbl {
    font-weight: 700; font-size: 11px; padding: 4px 10px;
    background: #f0f0f0; border-bottom: 1px solid #ccc;
    text-transform: uppercase; letter-spacing: 0.06em;
  }
  .dc-logistics-val { padding: 6px 12px; font-size: 12px; font-weight: 700; text-align: center; }

  /* ── ITEMS TABLE ── */
  .dc-items-tbl { width: 100%; border-collapse: collapse; border: 1.5px solid #000; border-top: none; font-size: 11px; }
  .dc-items-tbl thead tr { background: #e8e8e8; }
  .dc-items-tbl th {
    padding: 7px 6px; font-size: 11px; font-weight: 700;
    text-align: center; border: 1px solid #000;
    border-top: none;
    text-transform: uppercase; letter-spacing: 0.03em;
  }
  .dc-items-tbl th.left { text-align: left; }
  .dc-items-tbl td { padding: 8px 6px; border: 1px solid #000; text-align: center; vertical-align: top; }
  .dc-items-tbl td.left { text-align: left; }
  .dc-items-tbl td.right { text-align: right; font-weight: 700; }
  .dc-items-tbl td.qty-col { text-align: center; font-weight: 700; }
  .dc-item-desc-main { font-weight: 700; font-size: 12px; margin-bottom: 4px; }
  .dc-sub-item { font-size: 10px; color: #444; margin-top: 2px; padding: 2px 6px; background: #f8f8f8; border-left: 2px solid #ccc; }

  /* ── TOTAL ROW (light theme accent — matches other docs) ── */
  .dc-total-row { background: #e8e8e8; color: #000; font-weight: 700; }
  .dc-total-row td { border-color: #999; }

  /* ── DECLARATION ── */
  .dc-declaration { border: 1.5px solid #000; border-top: none; padding: 8px 12px; font-size: 10px; color: #444; line-height: 1.5; }

  /* ── SIGNATORY ── */
  .signatory-section { border: 1.5px solid #000; border-top: none; display: grid; grid-template-columns: 1fr 1fr; }
  .signatory-col { padding: 8px 14px 12px; border-right: 1.5px solid #000; text-align: center; font-size: 11px; }
  .signatory-col:last-child { border-right: none; }
  .signatory-space { height: 80px; display: flex; align-items: center; justify-content: center; position: relative; }
  .signatory-lbl { font-weight: 700; font-size: 12px; margin-top: 4px; }
  .signatory-sublbl { font-size: 11px; color: #555; margin-top: 2px; }
  .stamp-img { height: 70px; width: auto; object-fit: contain; opacity: 0.92; }
  .sign-img { height: 36px; width: auto; object-fit: contain; opacity: 0.9; position: absolute; bottom: 4px; right: 10px; }

  /* ── FOOTER ── */
  .dc-footer-center { text-align: center; font-size: 11px; font-weight: 700; border: 1.5px solid #000; border-top: none; padding: 7px; letter-spacing: 0.06em; }
  .dc-footer { margin-top: 6px; display: flex; justify-content: space-between; font-size: 10px; color: #666; }

  /* ── POD SECTION ── */
  .status-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; }

  /* ── PRINT STYLES ── */
  @media print {
    html, body {
      background: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body * { visibility: hidden; }
    .dc-wrap, .dc-wrap * {
      visibility: visible;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .dc-wrap {
      position: absolute;
      left: 0; top: 0;
      margin: 0; padding: 0;
      background: white;
      width: 100%;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .dc-sheet {
      display: flex !important;
      flex-direction: column !important;
      min-height: calc(297mm - 16mm) !important;
      box-shadow: none;
      border: none !important;
      padding: 0 !important;
      margin: 0 !important;
      max-width: 100%;
    }
    .print-border {
      display: block !important;
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      border: 1.5px solid #000;
      pointer-events: none;
      z-index: 10000;
    }
    .print-spacer { display: block !important; flex-grow: 1 !important; }
    .status-row { display: none !important; }
    .dc-items-tbl tr { page-break-inside: avoid; }
    .signatory-section, .dc-footer-center, .dc-footer {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    @page { margin: 8mm; size: A4; }
    .print-hide { display: none !important; }
    .address-block { margin-bottom: 0 !important; padding: 5px 10px !important; }
    .address-block * { font-size: 10px !important; line-height: 1.4 !important; }
    .dc-items-tbl th, .dc-items-tbl td {
      padding: 5px 6px !important;
      font-size: 10px !important;
      line-height: 1.35 !important;
    }
    .dc-total-row td { padding: 5px 6px !important; }
    .signatory-col { padding: 6px 10px !important; }
    .signatory-space { height: 50px !important; }
    .stamp-img { height: 45px !important; }
    .dc-declaration { padding: 6px 10px !important; font-size: 10px !important; }
    .dc-header-logo { padding: 6px 10px !important; }
    .dc-header-logo img { height: 32px !important; width: auto !important; align-self: flex-start !important; display: block !important; }
    .dc-header-title { font-size: 18px !important; }
    .dc-header-right { padding: 6px 10px !important; }
    .dc-challan-no { font-size: 11px !important; margin-bottom: 4px !important; padding-bottom: 4px !important; }
    .dc-meta-grid { font-size: 10px !important; }
    .dc-bill-left, .dc-bill-right { padding: 6px 10px !important; }
    .dc-section-lbl {
      margin-left: -10px !important;
      margin-right: -10px !important;
      margin-top: -6px !important;
      font-size: 9px !important;
    }
    .dc-supply-lbl, .dc-supply-val { padding: 4px 8px !important; font-size: 10px !important; }
    .dc-logistics-lbl { padding: 3px 6px !important; font-size: 10px !important; }
    .dc-logistics-val { padding: 4px 6px !important; font-size: 11px !important; }
  }
`;

// ─── POD Capture Dialog ───────────────────────────────────────────────────────
const PODCaptureDialog = ({ challan }: { challan: DeliveryChallan }) => {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [sign, setSign] = useState('');
    const [notes, setNotes] = useState('');

    const notify = useNotify();
    const refresh = useRefresh();
    const [update, { isPending }] = useUpdate();

    const handleSave = () => {
        update('delivery_challans', {
            id: challan.id,
            data: { status: 'delivered', pod_date: date, pod_signature: sign, pod_notes: notes },
            previousData: challan
        }, {
            onSuccess: () => {
                notify('POD Captured successfully', { type: 'success' });
                setOpen(false);
                refresh();
            },
            onError: (error: any) => {
                notify(`Failed to capture POD: ${error?.message || 'Unknown error'}`, { type: 'error' });
            }
        });
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#1a3a6e] hover:bg-[#1a3a6e]/90 text-white font-bold">
                    <PackageCheck className="w-4 h-4 mr-2" />
                    Capture POD
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle>Capture Proof of Delivery</DialogTitle></DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label>Delivery Date</Label>
                        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Received By (Name/Signature)</Label>
                        <Input placeholder="Enter recipient name" value={sign} onChange={(e) => setSign(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Remarks / Internal Notes</Label>
                        <Input placeholder="Any delivery notes..." value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} disabled={isPending} className="bg-emerald-600 text-white hover:bg-emerald-700">
                        {isPending ? 'Saving...' : 'Confirm Delivery'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ─── Title (Back Button + Name) ───────────────────────────────────────────────
const DCShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <>
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/delivery_challans">Delivery Challans</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>{record.reference}</Breadcrumb.PageItem>
            </Breadcrumb>
            <span className="text-xl font-bold tracking-tight">Delivery Challan {record.reference}</span>
        </>
    );
};

// ─── Actions Bar ──────────────────────────────────────────────────────────────
const DCShowActions = ({ onEmailClick, showWatermark, onToggleWatermark }: {
    onEmailClick: () => void;
    showWatermark: boolean;
    onToggleWatermark: () => void;
}) => {
    const record = useRecordContext() as DeliveryChallan;
    const dataProvider = useDataProvider<CrmDataProvider>();
    const notify = useNotify();
    const refresh = useRefresh();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    const handleGenerateInvoice = async () => {
        if (!record) return;
        setBusy(true);
        try {
            const result = await dataProvider.autoInvoiceFromDC([record.id as number]);
            const invoicedId = result.invoiceIds[0];
            if (invoicedId) {
                notify('Invoice generated.', { type: 'success' });
                navigate(`/customer_invoices/${invoicedId}/show`);
                return;
            }
            const errMsg = result.errors?.[0]?.error ?? 'Unknown error';
            notify(`Failed to generate invoice: ${errMsg}`, { type: 'error' });
            refresh();
        } catch (error) {
            notify(`Failed: ${(error as Error).message}`, { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const canInvoice = record && (record.status === 'sent' || record.status === 'delivered');

    return (
        <div className="flex items-center gap-2">
            <Button
                variant={showWatermark ? 'secondary' : 'outline'}
                size="sm"
                onClick={onToggleWatermark}
                className={showWatermark ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}
            >
                {showWatermark ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                Watermark: {showWatermark ? 'ON' : 'OFF'}
            </Button>
            <EditButton />
            <Button variant="outline" size="sm" onClick={onEmailClick}>
                <Mail className="w-4 h-4 mr-2" />
                Send Email
            </Button>
            <DCPDFButton />
            {record?.status === 'sent' && <PODCaptureDialog challan={record} />}
            {canInvoice && (
                <Button
                    size="sm"
                    onClick={handleGenerateInvoice}
                    disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                >
                    <FileText className="w-4 h-4 mr-2" />
                    {busy ? 'Generating…' : 'Generate Invoice'}
                </Button>
            )}
        </div>
    );
};

// ─── Show Content ─────────────────────────────────────────────────────────────
const DCShowContent = ({ showWatermark }: { showWatermark: boolean }) => {
    const record = useRecordContext() as DeliveryChallan;
    const config = useConfigurationContext();
    if (!record) return null;

    const items: any[] = record.items ?? [];
    const qtyByUnit = items.reduce((acc: Record<string, number>, it) => {
        const unit = it.unit ?? 'NOS';
        acc[unit] = (acc[unit] || 0) + (Number(it.quantity) || 0);
        return acc;
    }, {});
    const totalQtyLabel = Object.entries(qtyByUnit)
        .map(([unit, qty]) => `${qty} ${unit}`)
        .join(', ');

    return (
        <>
            <style>{css}</style>
            <div className="dc-wrap">
                <div className="dc-sheet">
                    <div className="print-border" />
                    <WatermarkOverlay isVisible={showWatermark} />

                    {/* ── HEADER: 2-column (logo+company | title+meta) ── */}
                    <div className="dc-header-bar">
                        <div className="dc-header-logo">
                            <img src="/logos/rovasys_horizontal_clean.png" alt="Rovasys" />
                            <div className="dc-company-name">ROVASYS ALLIED WORKS PVT. LTD.</div>
                            <div className="dc-company-addr">
                                W-145, S-Block, MIDC-Bhosari, Pune – 411026<br />
                                GST NUMBER: 27AAMCR6049R1ZD<br />
                                Contact No: +91 91722 01844
                            </div>
                        </div>
                        <div className="dc-header-right">
                            <div className="dc-header-title">Delivery Challan</div>
                            <div className="dc-challan-no">Challan No - {record.reference}</div>
                            <div className="dc-meta-grid">
                                <span className="dc-meta-lbl">Challan Date</span>
                                <span className="dc-meta-val"><DateField source="challan_date" /></span>
                                <span className="dc-meta-lbl">Challan Type</span>
                                <span className="dc-meta-val">{record.challan_type ?? 'Direct'}</span>
                                <span className="dc-meta-lbl">Vehicle No.</span>
                                <span className="dc-meta-val">{record.vehicle_no || '—'}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── FROM / TO ── */}
                    <div className="from-to-grid">
                        <AddressBlock 
                            label="Supplier Details"
                            addressOverride={
                                <>
                                    <strong>ROVASYS ALLIED WORKS PVT. LTD.</strong><br />
                                    W-145, S-Block, MIDC-Bhosari,<br />
                                    Pune, Maharashtra – 411026<br />
                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 80 }}>GSTIN:</span>
                                        <span>27AAMCR6049R1ZD</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 80 }}>PAN:</span>
                                        <span>AAMCR6049R</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 80 }}>State Code:</span>
                                        <span>27</span>
                                    </div>
                                </>
                            }
                        />

                        {/* TO */}
                        <div>
                            <AddressBlock label="Bill To" companyId={record.company_id}>
                                {(record.contact_id || record.contact_person || record.contact_no) && (
                                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #ddd', fontSize: 10 }}>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <span style={{ fontWeight: 700, minWidth: 90 }}>Contact Person:</span>
                                            <span>
                                                {/* X-3: name from linked contact (contact_id); legacy free-text fallback */}
                                                {record.contact_id ? (
                                                    <ReferenceField record={record} source="contact_id" reference="contacts" link={false}>
                                                        <TextField source="first_name" />{' '}<TextField source="last_name" />
                                                    </ReferenceField>
                                                ) : (record.contact_person || '-')}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <span style={{ fontWeight: 700, minWidth: 90 }}>Contact No:</span>
                                            <span>
                                                {record.contact_no
                                                    ? record.contact_no
                                                    : record.contact_id
                                                        ? <ReferenceField record={record} source="contact_id" reference="contacts" link={false}><DCContactPhone /></ReferenceField>
                                                        : '-'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </AddressBlock>
                        </div>
                    </div>

                    {/* ── PLACE OF SUPPLY ── */}
                    {record.ship_to_address && (
                        <div className="dc-supply-bar">
                            <div className="dc-supply-lbl">Place of supply:</div>
                            <div className="dc-supply-val">{record.ship_to_address}</div>
                        </div>
                    )}

                    {/* ── LOGISTICS BAR ── */}
                    <div className="dc-logistics-bar">
                        <div className="dc-logistics-col">
                            <div className="dc-logistics-lbl">Mode of Transport</div>
                            <div className="dc-logistics-val">{record.mode_of_transport || '-'}</div>
                        </div>
                        <div className="dc-logistics-col">
                            <div className="dc-logistics-lbl">Vehicle No.</div>
                            <div className="dc-logistics-val">{record.vehicle_no || 'NA'}</div>
                        </div>
                        <div className="dc-logistics-col">
                            <div className="dc-logistics-lbl">Delivery Terms</div>
                            <div className="dc-logistics-val">{record.delivery_terms || '-'}</div>
                        </div>
                    </div>

                    {/* ── ITEMS TABLE ── */}
                    <table className="dc-items-tbl">
                        <thead>
                            <tr>
                                <th style={{ width: 70, whiteSpace: 'nowrap' }}>Sr. No.</th>
                                <th className="left">Product Description</th>
                                <th style={{ width: 120 }}>Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item: any, i: number) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td className="left">
                                        <div className="dc-item-desc-main">{item.description}</div>
                                        {(item.sub_items ?? []).map((sub: any, si: number) => (
                                            <div key={si} className="dc-sub-item">
                                                {si + 1}) {sub.text ?? sub}
                                                {sub.qty_note && <strong style={{ marginLeft: 6 }}>TOTAL {sub.qty_note}</strong>}
                                            </div>
                                        ))}
                                    </td>
                                    <td className="qty-col">{item.quantity} {item.unit ?? 'NOS'}</td>
                                </tr>
                            ))}
                            <tr className="print-hide" style={{ height: 32 }}><td colSpan={3} style={{ borderBottom: 'none' }} /></tr>
                        </tbody>
                        <tfoot>
                            <tr className="dc-total-row">
                                <td></td>
                                <td className="left">Total</td>
                                <td className="qty-col">{totalQtyLabel}</td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className="print-spacer" />

                    <div className="footer-group">
                        {/* ── DECLARATION ── */}
                        <div className="dc-declaration">
                            Declaration: Delivery Challan issued under Rule 55 of CGST Rules for transportation of goods without invoice.
                        </div>

                        {/* ── SIGNATORY ── */}
                        <div className="signatory-section">
                            <div className="signatory-col">
                                <div className="signatory-space" />
                                <div className="signatory-lbl">Receiver's Stamp &amp; Signature</div>
                            </div>
                            <div className="signatory-col">
                                <div className="signatory-space">
                                    <img src="/img/rovasys-stamp.png" alt="Company Stamp" className="stamp-img" />
                                </div>
                                <div className="signatory-lbl">Authorised Signatory</div>
                                <div className="signatory-sublbl">Rovasys Allied Works Pvt Ltd</div>
                            </div>
                        </div>

                        <div className="dc-footer-center">
                            System generated Document, Does not require any signature.
                        </div>
                        <div className="dc-footer">
                            <span>Page: 1 / 1</span>
                            {config.showPrintTimestamp && (
                                <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── POD SECTION (internal tracking, outside print area) ──
                <div style={{ maxWidth: 960, margin: '20px auto 0', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: 16, background: '#f8fafc' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1a3a6e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                        Proof of Delivery (POD)
                    </div>
                    {record.status === 'delivered' ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Delivery Date</span>
                                <span className="text-sm font-black text-[#1a3a6e]">{record.pod_date ? new Date(record.pod_date).toLocaleDateString() : '-'}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Received By / Sign</span>
                                <span className="text-sm font-black text-[#1a3a6e]">{record.pod_signature || 'N/A'}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</span>
                                <div className="flex items-center gap-2 text-emerald-600 font-black text-xs">
                                    <PackageCheck className="w-4 h-4" /> DELIVERED
                                </div>
                            </div>
                            {record.pod_notes && (
                                <div className="md:col-span-3 mt-2 pt-2 border-t border-slate-200">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">POD Notes</span>
                                    <p className="text-xs text-slate-600 italic">"{record.pod_notes}"</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center py-4 space-y-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border shadow-sm">
                                <ClipboardCheck className="w-6 h-6 text-slate-300" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-bold text-slate-600">POD Receipt Pending</p>
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Capture signature and date once delivered</p>
                            </div>
                            <PODCaptureDialog challan={record} />
                        </div>
                    )}
                </div>
                */}
            </div>
        </>
    );
};

// ─── Export ───────────────────────────────────────────────────────────────────
export const DCShow = () => {
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [showWatermark, setShowWatermark] = useState(true);

    return (
        <Show disableBreadcrumb title={<DCShowTitle />} actions={<DCShowActions onEmailClick={() => setEmailModalOpen(true)} showWatermark={showWatermark} onToggleWatermark={() => setShowWatermark(!showWatermark)} />}>
            <DCShowContent showWatermark={showWatermark} />
            <DCEmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />
        </Show>
    );
};
